/**
 * Phase 2 personalization catalog — the curated options an owner can pick
 * from in "Business Config" to override the automatic (logo/category-derived)
 * accent color and font. Kept as a fixed, small set (rather than a free color
 * picker or arbitrary font input) so every choice stays legible and on-brand
 * for the platform — no accessibility/contrast validation needed.
 */

export interface BrandColorSwatch {
  /** Display name shown in the picker + read-only view. */
  label: string;
  /** Hex value; null represents "Auto" (derive from logo/category). */
  hex: string | null;
}

export const BRAND_COLOR_SWATCHES: BrandColorSwatch[] = [
  { label: "Auto", hex: null },
  { label: "Violet", hex: "#6a11cb" },
  { label: "Teal", hex: "#0d9488" },
  { label: "Amber", hex: "#b45309" },
  { label: "Terracotta", hex: "#c2410c" },
  { label: "Indigo", hex: "#4338ca" },
  { label: "Blue", hex: "#1d4ed8" },
  { label: "Rose", hex: "#be185d" },
  { label: "Emerald", hex: "#15803d" },
];

export interface BrandFontOption {
  /** Stored in businessConfig.displayFont.value (business_config's
   *  display_font jsonb column — bare {value?: string}, no is_enabled, same
   *  as brandColor). Absent = "default". */
  value: string;
  label: string;
  /** CSS font-family stack applied to the business name heading. */
  stack: string;
  /** Google Fonts `family=` query param (undefined for the system default). */
  googleFontParam?: string;
}

export const BRAND_FONT_OPTIONS: BrandFontOption[] = [
  {
    value: "default",
    label: "Default",
    stack: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  {
    value: "playfair",
    label: "Playfair (elegant serif)",
    stack: "'Playfair Display', Georgia, serif",
    googleFontParam: "Playfair+Display:wght@700;800",
  },
  {
    value: "poppins",
    label: "Poppins (modern sans)",
    stack: "'Poppins', -apple-system, sans-serif",
    googleFontParam: "Poppins:wght@600;700",
  },
  {
    value: "grotesk",
    label: "Space Grotesk (bold)",
    stack: "'Space Grotesk', -apple-system, sans-serif",
    googleFontParam: "Space+Grotesk:wght@600;700",
  },
];

export function getFontOption(value: string | null | undefined): BrandFontOption {
  return BRAND_FONT_OPTIONS.find((f) => f.value === value) ?? BRAND_FONT_OPTIONS[0];
}
